import { gql, type TypedDocumentNode } from '@apollo/client';
import { useState, type ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Rarity } from '@sorare/core/src/__generated__/globalTypes';
import { Skeleton as SkeletonDumb } from '@sorare/core/src/atoms/animations/Skeleton';
import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex';
import {
  BrandM,
  LabelL,
  LabelM,
  LabelS,
} from '@sorare/core/src/atoms/typography';
import { HorizontalDivider } from '@sorare/core/src/atoms/ui/Divider';
import { Card } from '@sorare/core/src/components/card/Card';
import { GlowCard } from '@sorare/core/src/components/card/GlowCard';
import { SorareUser } from '@sorare/core/src/components/user/SorareUser';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { useIsMobileApp } from '@sorare/core/src/hooks/useIsMobileApp';
import { useMonetaryAmount } from '@sorare/core/src/hooks/useMonetaryAmount';
import { getHumanReadableSerialNumber } from '@sorare/core/src/lib/cards';
import { getRarityRGBColor } from '@sorare/core/src/lib/colors';
import { useEvents } from '@sorare/core/src/lib/events/useEvents';
import { cart } from '@sorare/core/src/lib/glossary';
import type {
  ClickAfterPurchase_Action,
  ClickInstantBuy_Flow,
} from '@sorare/core/src/protos/events/so5/shared/events';
import { ClickAddToCart_Origin } from '@sorare/core/src/protos/events/so5/shared/events';

import { SelectRarity } from '#src/atoms/SelectRarity/index.tsx';
import { AddToCart } from '#src/components/cart/AddToCart/index.tsx';
import { DiscountedPrice } from '#src/components/marketCredit/DiscountedPrice/index.tsx';
import { PrimaryBuyBuyField } from '#src/components/primaryBuy/PrimaryBuyBuyField/index.tsx';

import { BottomGlow } from './BottomGlow/index.tsx';
import type {
  AvailablePrimaryBuyForAPlayer_anyCard,
  AvailablePrimaryOfferForAPlayerQuery,
  AvailablePrimaryOfferForAPlayerQueryVariables,
} from './__generated__/index.graphql.ts';

const Root = styled(Horizontal).attrs({ gap: 4 })`
  position: relative;
  isolation: isolate;
  background: conic-gradient(
      from 90deg at 3px 3px,
      var(--c-nd-50) 25%,
      rgba(0, 0, 0, 1) 0
    )
    0 0/5px 5px;
  margin: 0 calc(-1 * var(--container-padding));
  padding: var(--triple-unit) var(--container-padding);
`;

const PositionBottomGlow = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  width: 100%;
`;

const TokenContainer = styled.div`
  display: flex;
  flex-shrink: 0;
  width: 130px;
`;
const Bottom = styled(Vertical).attrs({ gap: 3 })`
  flex-grow: 1;
  justify-content: flex-end;
  width: 100%;
`;
const Content = styled(Horizontal).attrs({ gap: 2 })`
  align-items: flex-start;
`;
const TokenBuy = styled(Vertical).attrs({ gap: 3 })`
  align-items: flex-start;
  flex-grow: 1;
  height: 100%;
`;
const FullSBHorizontal = styled(SBHorizontal)`
  width: 100%;
  align-items: flex-start;
`;
const Skeleton = styled(SkeletonDumb)`
  height: 210px;
  width: 100%;
`;

type Action = {
  onClick?: () => void;
  to?: string;
  label: ReactNode;
  action: ClickAfterPurchase_Action;
};

type PrimaryBuyActions = {
  primary?: Action;
  secondary?: Action;
};

type Props = {
  playerSlug: string;
  title: ReactNode;
  scoreLine?: ReactNode;
  description: ReactNode;
  className?: string;
  primaryBuyActions: PrimaryBuyActions;
  origin: ClickInstantBuy_Flow;
  gameSlug?: string;
};

const cardFragment = gql`
  fragment AvailablePrimaryBuyForAPlayer_anyCard on AnyCardInterface {
    slug
    livePrimaryOffer {
      id
    }
    ...getHumanReadableSerialNumber_anyCard
    ...PrimaryBuyBuyField_anyCard
    ...Card_anyCard
    ...AddToCart_anyCard
  }
  ${PrimaryBuyBuyField.fragments.anyCard}
  ${Card.fragments.anyCard}
  ${AddToCart.fragments.anyCard}
  ${getHumanReadableSerialNumber.fragments.anyCard}
` as TypedDocumentNode<AvailablePrimaryBuyForAPlayer_anyCard>;

const AVAILABLE_PRIMARY_OFFER_FOR_A_PLAYER_QUERY = gql`
  query AvailablePrimaryOfferForAPlayerQuery(
    $withPrimaryOffer: Boolean! = true
    $playerSlug: String!
    $googlePlayStoreCountryCode: String
    $isAndroidApp: Boolean = false
    $isIosApp: Boolean = false
    $withCollectionPredictions: Boolean = false
  ) {
    anyPlayer(slug: $playerSlug) {
      slug
      limited: anyCardWithLivePrimaryOffer(rarity: limited) {
        slug
        ...AvailablePrimaryBuyForAPlayer_anyCard
      }
      rare: anyCardWithLivePrimaryOffer(rarity: rare) {
        slug
        ...AvailablePrimaryBuyForAPlayer_anyCard
      }
      superRare: anyCardWithLivePrimaryOffer(rarity: super_rare) {
        slug
        ...AvailablePrimaryBuyForAPlayer_anyCard
      }
    }
  }
  ${cardFragment}
` as TypedDocumentNode<
  AvailablePrimaryOfferForAPlayerQuery,
  AvailablePrimaryOfferForAPlayerQueryVariables
>;

const AVAILABLE_RARITIES: Rarity[] = [
  Rarity.limited,
  Rarity.rare,
  Rarity.super_rare,
];

export const AvailablePrimaryBuyForAPlayer = ({
  playerSlug,
  title,
  scoreLine,
  description,
  className,
  primaryBuyActions: primaryBuyActionsFromProps,
  origin,
  gameSlug,
}: Props) => {
  const track = useEvents();
  const { isAndroidApp, isIosApp, googlePlayStoreCountryCode } =
    useIsMobileApp();
  const { toMonetaryAmount } = useMonetaryAmount();
  const [rarity, setRarity] = useState<Rarity>(AVAILABLE_RARITIES[0]);

  const { data, loading } = useQuery(
    AVAILABLE_PRIMARY_OFFER_FOR_A_PLAYER_QUERY,
    {
      variables: {
        playerSlug,
        isAndroidApp,
        isIosApp,
        googlePlayStoreCountryCode,
      },
    }
  );

  const getCard = (r: Rarity) => {
    switch (r) {
      case Rarity.limited:
        return data?.anyPlayer?.limited;
      case Rarity.rare:
        return data?.anyPlayer?.rare;
      case Rarity.super_rare:
        return data?.anyPlayer?.superRare;
      default:
        return null;
    }
  };

  const availableRarities = AVAILABLE_RARITIES.filter(r => getCard(r));

  const selectedCard = getCard(rarity);

  if (loading) {
    return <Skeleton />;
  }

  if (!selectedCard?.livePrimaryOffer?.price) {
    return null;
  }

  const { livePrimaryOffer } = selectedCard;
  const { id } = livePrimaryOffer;

  const trackBuyPrimaryBuyAction = (action: ClickAfterPurchase_Action) => {
    track('Click After Purchase', {
      offerId: id,
      cardSlug: selectedCard.slug,
      gameSlug,
      sport: selectedCard.sport,
      action,
    });
  };
  const { primary, secondary } = primaryBuyActionsFromProps;

  const primaryBuyActions = {
    ...(!!primary && {
      primary: {
        label: primary.label,
        onClick: () => {
          primary?.onClick?.();
          trackBuyPrimaryBuyAction(primary.action);
        },
        to: primary.to,
      },
    }),
    ...(secondary && {
      secondary: {
        label: secondary.label,
        onClick: () => {
          secondary?.onClick?.();
          trackBuyPrimaryBuyAction(secondary.action);
        },
        to: secondary.to,
      },
    }),
  };

  return (
    <Root gap={2} className={className}>
      <PositionBottomGlow>
        <BottomGlow rarity={selectedCard.rarityTyped} />
      </PositionBottomGlow>
      <TokenContainer>
        <GlowCard active color={getRarityRGBColor(selectedCard.rarityTyped)}>
          <Card card={selectedCard} width={320} />
        </GlowCard>
      </TokenContainer>
      <TokenBuy>
        <FullSBHorizontal gap={2}>
          <Vertical gap={0.5}>
            <BrandM bold>{title}</BrandM>
            <Content gap={2}>
              <Vertical>
                {!!scoreLine && (
                  <LabelM color="var(--c-onBase-secondary)">{scoreLine}</LabelM>
                )}
                <LabelL color="var(--c-onBase-secondary)">{description}</LabelL>
              </Vertical>
            </Content>
          </Vertical>
          <SelectRarity
            rarities={availableRarities}
            selectedRarity={rarity}
            onChange={setRarity}
            buttonSize="small"
          />
        </FullSBHorizontal>
        <HorizontalDivider />
        <Bottom>
          <Vertical center gap={0.5}>
            <Horizontal>
              <DiscountedPrice
                canDiscount
                price={toMonetaryAmount(livePrimaryOffer.price)}
                sport={selectedCard.sport}
              />
            </Horizontal>
            <LabelS color="var(--c-onBase-secondary)">
              {getHumanReadableSerialNumber(selectedCard)} - <SorareUser />
            </LabelS>
          </Vertical>
          <Horizontal>
            <AddToCart
              size="medium"
              anyCard={selectedCard}
              color="tertiary"
              fullWidth
              origin={ClickAddToCart_Origin.MATCH_VIEW}
            >
              <FormattedMessage {...cart.addToCart} />
            </AddToCart>
            <PrimaryBuyBuyField
              origin={origin}
              color="secondary"
              size="medium"
              fullWidth
              card={selectedCard}
              primaryBuyConfirmationOptions={{
                actions: primaryBuyActions,
              }}
            />
          </Horizontal>
        </Bottom>
      </TokenBuy>
    </Root>
  );
};
